import { FC, useEffect, useState } from "react";
import { ConnectionStatus } from "@/models/ticker";
import { TeamReference } from "@/models/pdfData";
import {
  Box,
  Palette,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Scoreboard, SwapHoriz, Timer, Videocam } from "@mui/icons-material";
import { useGameDataContext } from "@context/GameDataContext";
import { remainingChallenges } from "@/utils/challenge-utils";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { GameProgress, SetStat } from "@/models/gameState";
import { gameState, useTeamSides } from "@/services/gameState";
import { useAppDataContext } from "@context/AppContext";
import { otherTeam } from "@/pdf/utils";
import {
  AlternatingBackgroundTableRow,
  CenteredHeaderTableCell,
  CenteredTableCell,
} from "../../table/TableComponents";

const getRemainingRequests = (
  request:
    | "timeoutsRemaining"
    | "substitutionsRemaining"
    | "challengesRemaining",
  currentSet?: SetStat,
  team?: TeamReference,
): string => {
  if (team && currentSet) {
    return currentSet[request][team].toString();
  } else {
    return "-";
  }
};

const getRemainingChallenges = (currentSet?: SetStat, team?: TeamReference) =>
  getRemainingRequests("challengesRemaining", currentSet, team);

const getRemainingTimeouts = (currentSet?: SetStat, team?: TeamReference) =>
  getRemainingRequests("timeoutsRemaining", currentSet, team);

const getRemainingSubstitutions = (
  currentSet?: SetStat,
  team?: TeamReference,
) => getRemainingRequests("substitutionsRemaining", currentSet, team);

const NumberCell: FC<{
  value: string;
  id: string;
  palette: Palette;
  gameData: {
    isCurrentSet: boolean;
    gameProgress: GameProgress;
    showCurrentSetOnly: boolean;
  };
}> = ({
  value,
  id,
  palette,
  gameData: { isCurrentSet, showCurrentSetOnly, gameProgress },
}) => {
  const backgroundColor =
    (isCurrentSet &&
      (value === "0"
        ? palette.error.main
        : value === "1"
          ? palette.warning.light
          : undefined)) ||
    undefined;
  return (
    <CenteredTableCell key={id}>
      <Box
        sx={{
          backgroundColor,
          borderRadius: "3px",
          opacity:
            (isCurrentSet && !showCurrentSetOnly) ||
            gameProgress >= GameProgress.GAME_FINISHED
              ? 1
              : 0.8,
        }}
      >
        {value}
      </Box>
    </CenteredTableCell>
  );
};

const GameOverviewTable: FC<{
  showCurrentSetOnly: boolean;
  showPoints?: boolean;
  showChallenges: boolean;
}> = ({ showCurrentSetOnly, showPoints = false, showChallenges }) => {
  const { palette } = useTheme();
  const { onTablet } = useResponsiveContext();
  const { game } = useGameDataContext();
  const connectionStatus = gameState("connectionStatus");
  const currentSet = gameState("currentSet");
  const currentScore = gameState("currentScore");
  const playedSets = gameState("playedSets");
  const gameProgress = gameState("gameProgress");
  const scoreConfiguration = gameState("scoreConfiguration");
  const { useFirstRefPerspective, challengeOverrideRemaining, currentView } =
    useAppDataContext();
  const { leftTeam, rightTeam } = useTeamSides({ useFirstRefPerspective });

  const [allSets, setAllSets] = useState<{ [key in number]: SetStat }>({});

  useEffect(() => {
    setAllSets({ ...playedSets, [currentSet.setNumber]: currentSet });
  }, [currentSet, playedSets]);

  const sets = Array.from(
    {
      length:
        Object.values(playedSets).length +
        (gameProgress >= GameProgress.GAME_FINISHED ? 0 : 1),
    },
    (_, i) => i + 1,
  );

  const remainingChallengesInSet = game
    ? remainingChallenges(game, sets)
    : undefined;

  const columns: { name: string; team?: TeamReference }[] = [
    ...(showChallenges ? [{ name: "Challenge", team: leftTeam }] : []),
    { name: "Timeout", team: leftTeam },
    { name: "Substitutions", team: leftTeam },
    ...(showPoints ? [{ name: "Points", team: leftTeam }] : []),
  ];

  const headers: { name: string; team?: TeamReference }[] = [
    ...columns,
    { name: "Satz", team: undefined },
    ...columns.reverse().map((c) => ({ name: c.name, team: rightTeam })),
  ];

  const getTableRowStyle = (
    isCurrentSet: boolean,
    connectionStatus: ConnectionStatus,
  ): SxProps => {
    const connectionStatusBoxShadow: { [key in ConnectionStatus]: string } = {
      connected: isCurrentSet
        ? `0px 0px 0px 2px ${palette.text.secondary}`
        : "",
      loosing: isCurrentSet ? `0px 0px 0px 2px ${palette.text.disabled}` : "",
      paused: isCurrentSet ? `0px 0px 0px 2px ${palette.text.disabled}` : "",
      lost: ``,
    };

    return {
      transition: "border 5s linear",
      boxShadow: connectionStatusBoxShadow[connectionStatus],
    };
  };

  const iconSx: SxProps = {
    verticalAlign: "middle",
    fontSize: onTablet ? "clamp(15px, 4vw, 24px)" : "24px",
  };

  return (
    <TableContainer>
      {!showCurrentSetOnly && (
        <Typography textAlign={"center"} variant={"h6"}>
          Verbleibende Anträge:
        </Typography>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map((s, index) => {
              switch (s.name) {
                case "Challenge":
                  return (
                    <CenteredHeaderTableCell
                      key={`challenge-table-cell-${index}`}
                    >
                      <Videocam sx={iconSx} />
                    </CenteredHeaderTableCell>
                  );
                case "Timeout":
                  return (
                    <CenteredHeaderTableCell
                      key={`timeout-table-cell-${index}`}
                    >
                      <Timer sx={iconSx} />
                    </CenteredHeaderTableCell>
                  );
                case "Substitutions":
                  return (
                    <CenteredHeaderTableCell
                      key={`substitutions-table-cell-${index}`}
                    >
                      <SwapHoriz sx={iconSx} />
                    </CenteredHeaderTableCell>
                  );
                case "Points":
                  return (
                    <CenteredHeaderTableCell
                      key={`challenge-table-cell-${index}`}
                    >
                      <Scoreboard sx={iconSx} />
                    </CenteredHeaderTableCell>
                  );
                default:
                  return (
                    <CenteredHeaderTableCell
                      key={`${s.name}-table-cell-${index}`}
                      sx={{
                        fontSize: onTablet
                          ? "clamp(10px, 1.5vw, 14px)"
                          : "14px",
                      }}
                    >
                      {s.name}
                    </CenteredHeaderTableCell>
                  );
              }
            })}
          </TableRow>
        </TableHead>
        <TableBody sx={{ borderCollapse: "collapse" }}>
          {sets
            .filter(
              (set) => !showCurrentSetOnly || set === currentSet?.setNumber,
            )
            .map((set) => {
              const isCurrentSet = set === (currentSet?.setNumber || 0);
              const setOfRow = allSets[set];
              return (
                <AlternatingBackgroundTableRow
                  key={`set-row-${set}`}
                  sx={getTableRowStyle(isCurrentSet, connectionStatus)}
                >
                  {headers.map((column) => {
                    switch (column.name) {
                      case "Challenge":
                        return (
                          <NumberCell
                            value={
                              (!scoreConfiguration.videoChallengeAvailable ||
                                challengeOverrideRemaining) &&
                              currentView === "challenge"
                                ? (column.team &&
                                    remainingChallengesInSet?.[set]?.[
                                      column.team
                                    ]?.toString()) ||
                                  "-"
                                : getRemainingChallenges(setOfRow, column.team)
                            }
                            palette={palette}
                            gameData={{
                              isCurrentSet,
                              showCurrentSetOnly,
                              gameProgress,
                            }}
                            id={`${column.team}-${set}-challenges`}
                            key={`${column.team}-${set}-challenges`}
                          />
                        );
                      case "Timeout":
                        return (
                          <NumberCell
                            value={getRemainingTimeouts(setOfRow, column.team)}
                            palette={palette}
                            gameData={{
                              isCurrentSet,
                              showCurrentSetOnly,
                              gameProgress,
                            }}
                            id={`${column.team}-${set}-timeouts`}
                            key={`${column.team}-${set}-timeouts`}
                          />
                        );
                      case "Substitutions":
                        return (
                          <NumberCell
                            value={getRemainingSubstitutions(
                              setOfRow,
                              column.team,
                            )}
                            palette={palette}
                            gameData={{
                              isCurrentSet,
                              showCurrentSetOnly,
                              gameProgress,
                            }}
                            id={`${column.team}-${set}-substitutions`}
                            key={`${column.team}-${set}-substitutions`}
                          />
                        );
                      case "Satz":
                        return (
                          <CenteredTableCell key={`${set}-set`}>
                            <Box
                              sx={{
                                backgroundColor:
                                  isCurrentSet &&
                                  !showCurrentSetOnly &&
                                  gameProgress < GameProgress.GAME_FINISHED
                                    ? palette.success.light
                                    : "none",
                                borderRadius: "3px",
                                opacity:
                                  (isCurrentSet && !showCurrentSetOnly) ||
                                  gameProgress >= GameProgress.GAME_FINISHED
                                    ? 1
                                    : 0.8,
                              }}
                            >
                              {set}
                            </Box>
                          </CenteredTableCell>
                        );
                      case "Points":
                        return (
                          <CenteredTableCell
                            key={`${column.team}-${set}--points`}
                          >
                            <Box
                              sx={{
                                backgroundColor:
                                  column.team &&
                                  (setOfRow?.result?.[column.team] >
                                    setOfRow?.result?.[
                                      otherTeam(column.team)
                                    ] ||
                                    (gameProgress >=
                                      GameProgress.GAME_FINISHED &&
                                      isCurrentSet &&
                                      currentScore[column.team] >
                                        currentScore[otherTeam(column.team)]))
                                    ? palette.success.light
                                    : "none",
                                borderRadius: "3px",
                              }}
                            >
                              {(column.team &&
                                (isCurrentSet
                                  ? currentScore[column.team]
                                  : setOfRow?.result?.[column.team]
                                )?.toString()) ||
                                "-"}
                            </Box>
                          </CenteredTableCell>
                        );
                    }
                  })}
                </AlternatingBackgroundTableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GameOverviewTable;
